import { getIxdForwardRun, startIxdForward } from "@/apis/ixd-forward/route";
import { IxdForwardRun, IxdForwardStep } from "@/apis/ixd-forward/types";
import { ModalWrapper } from "@/components/modals/modal-wrapper";
import { useOpenReport } from "@/hooks/use-open-report";
import { popModal, pushModal } from "@/stores/modal";
import { Button } from "@/toggle-ui-kit/button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, FileUp, Loader2, X, XCircle } from "lucide-react";
import { useRef, useState } from "react";

// ============================================
// IxdForwardModal
// DSD·IXD 두 파일을 받아 태깅된 워크북을 만든다. 시작한 뒤에도 모달을
// 닫지 않고 완료까지 진행 상태를 보여준다 — 수십 분 걸리는 작업이라 닫아 두면
// 사용자가 끝났는지 알 방법이 없다. 완료된 워크북은 일반 워크북과 똑같이 목록에
// 나오므로, 여기서는 바로 열 수 있는 링크만 준다.
// ============================================

const POLL_INTERVAL_MS = 5000;

/** 파이프라인 단계 라벨 — 지금 무엇이 도는지만 말한다(내부 단계명은 그대로 안 쓴다). */
const STEP_LABELS: Record<IxdForwardStep, string> = {
  QUEUED: "대기 중",
  ANALYZE_PRIOR_DSD: "보고서 분석 중",
  INJECT_PRIOR_TAGS: "태그 옮기는 중",
  FORWARD_TABLES: "표 태깅 중",
  DONE: "완료",
};

const STEP_ORDER: IxdForwardStep[] = [
  "QUEUED",
  "ANALYZE_PRIOR_DSD",
  "INJECT_PRIOR_TAGS",
  "FORWARD_TABLES",
];

const isActive = (run: IxdForwardRun) =>
  run.status === "PENDING" || run.status === "RUNNING";

interface FileSlotProps {
  label: string;
  description: string;
  accept: string;
  file: File | null;
  onSelect: (file: File | null) => void;
  disabled: boolean;
}

const FileSlot = ({
  label,
  description,
  accept,
  file,
  onSelect,
  disabled,
}: FileSlotProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex items-center gap-3 rounded-lg border border-line-normal p-3">
      <div className="flex flex-1 min-w-0 flex-col">
        <span className="label-1-normal-medium text-label-normal">{label}</span>
        {file ? (
          <span className="caption-1-regular text-label-neutral truncate">
            {file.name}
          </span>
        ) : (
          <span className="caption-1-regular text-label-alternative">
            {description}
          </span>
        )}
      </div>
      {file ? (
        <button
          type="button"
          onClick={() => onSelect(null)}
          disabled={disabled}
          className="text-label-alternative hover:text-label-normal"
        >
          <X className="h-4 w-4" />
        </button>
      ) : (
        <Button
          variant="outlined"
          size="small"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
        >
          파일 선택
        </Button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          onSelect(e.target.files?.[0] ?? null);
          e.target.value = "";
        }}
      />
    </div>
  );
};

/** 시작 후 완료까지 — 단계 목록과 결과 링크. */
const ProgressView = ({ run }: { run: IxdForwardRun }) => {
  const openReport = useOpenReport();
  const currentIndex = STEP_ORDER.indexOf(run.step);

  if (run.status === "FAILED") {
    return (
      <div className="flex flex-col gap-2 rounded-lg border border-line-normal p-4">
        <div className="flex items-center gap-2">
          <XCircle className="h-4 w-4 text-status-negative" />
          <span className="label-1-normal-medium text-label-normal">
            태깅에 실패했습니다
          </span>
        </div>
        <p className="caption-1-regular text-label-neutral">
          파일을 확인하고 다시 시도해 주세요. 계속 실패하면 파일과 함께 문의해
          주세요.
        </p>
      </div>
    );
  }

  if (run.status === "SUCCEEDED") {
    return (
      <div className="flex flex-col gap-3 rounded-lg border border-line-normal p-4">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-status-positive" />
          <span className="label-1-normal-medium text-label-normal">
            태깅이 끝났습니다
          </span>
        </div>
        <p className="caption-1-regular text-label-neutral">
          워크북 목록에서도 열 수 있습니다.
        </p>
        {run.priorWorkbookId ? (
          <Button
            variant="solid"
            size="small"
            onClick={() => {
              openReport(run.priorWorkbookId!);
              popModal();
            }}
          >
            워크북 열기
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line-normal p-4">
      {STEP_ORDER.map((step, index) => {
        const done = index < currentIndex;
        const now = index === currentIndex;
        return (
          <div key={step} className="flex items-center gap-2">
            {done ? (
              <CheckCircle2 className="h-4 w-4 text-status-positive" />
            ) : now ? (
              <Loader2 className="h-4 w-4 animate-spin text-label-neutral" />
            ) : (
              <span className="h-4 w-4 rounded-full border border-line-normal" />
            )}
            <span
              className={
                now
                  ? "label-1-normal-medium text-label-normal"
                  : "caption-1-regular text-label-alternative"
              }
            >
              {STEP_LABELS[step]}
            </span>
          </div>
        );
      })}
      <p className="caption-1-regular text-label-alternative">
        표가 많으면 수십 분 걸립니다. 이 창을 닫아도 작업은 계속됩니다.
      </p>
    </div>
  );
};

export const IxdForwardModal = () => {
  const queryClient = useQueryClient();
  const [priorDsd, setPriorDsd] = useState<File | null>(null);
  const [priorIxd, setPriorIxd] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(null);

  const {
    mutate: start,
    isPending,
    error,
  } = useMutation({
    mutationFn: () =>
      startIxdForward({
        priorDsd: priorDsd!,
        priorIxd: priorIxd!,
        onIxdUploadProgress: (ratio) =>
          setUploadProgress(`파일 올리는 중 (${Math.round(ratio * 100)}%)`),
      }),
    onSuccess: (run) => {
      setRunId(run.runId);
      queryClient.invalidateQueries({ queryKey: ["ixd-forward-runs"] });
    },
    onSettled: () => setUploadProgress(null),
  });

  const { data: run } = useQuery({
    queryKey: ["ixd-forward-run", runId],
    queryFn: () => getIxdForwardRun(runId!),
    enabled: runId !== null,
    refetchInterval: (query) =>
      query.state.data && isActive(query.state.data) ? POLL_INTERVAL_MS : false,
  });

  const started = runId !== null;
  const ready = priorDsd && priorIxd && !isPending;
  const finished = run?.status === "SUCCEEDED" || run?.status === "FAILED";

  return (
    <ModalWrapper className="flex w-[520px] flex-col gap-5 rounded-2xl bg-white p-6 shadow-2xl">
      <div className="flex flex-col gap-1">
        <h2 className="title-3-bold text-label-normal">IXD로 불러오기</h2>
        <p className="body-2-normal-regular text-label-neutral">
          공시 완성본(IXD)의 태깅을 그 보고서의 DSD 워크북으로 옮깁니다. 완성된
          워크북은 롤포워드의 직전본으로 씁니다.
        </p>
      </div>

      {started ? (
        run ? (
          <ProgressView run={run} />
        ) : (
          <div className="flex items-center gap-2 rounded-lg border border-line-normal p-4">
            <Loader2 className="h-4 w-4 animate-spin text-label-neutral" />
            <span className="caption-1-regular text-label-neutral">
              시작하는 중...
            </span>
          </div>
        )
      ) : (
        <div className="flex flex-col gap-2">
          <FileSlot
            label="DSD 파일"
            description="공시에 사용한 DSD (.dsd)"
            accept=".dsd"
            file={priorDsd}
            onSelect={setPriorDsd}
            disabled={isPending}
          />
          <FileSlot
            label="IXD 파일"
            description="같은 보고서의 공시 완성본 (.ixd)"
            accept=".ixd"
            file={priorIxd}
            onSelect={setPriorIxd}
            disabled={isPending}
          />
        </div>
      )}

      {error ? (
        <p className="caption-1-regular text-status-negative">
          시작에 실패했습니다. 파일을 확인하고 다시 시도해 주세요.
        </p>
      ) : null}

      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outlined"
          size="base"
          onClick={() => popModal()}
          disabled={isPending}
        >
          {started && !finished ? "닫기" : "취소"}
        </Button>
        {started ? null : (
          <Button
            variant="solid"
            size="base"
            leftIcon={
              isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileUp className="h-4 w-4" />
              )
            }
            onClick={() => start()}
            disabled={!ready}
          >
            {isPending ? (uploadProgress ?? "시작 중...") : "불러오기 시작"}
          </Button>
        )}
      </div>
    </ModalWrapper>
  );
};

export const openIxdForwardModal = () => pushModal(IxdForwardModal, {});
